/**
 * Shared table utility components: Pagination, ErrorBanner, LoadingIndicator,
 * and common table styles used across admin list pages.
 */

import { t } from "../i18n";

// ─── Pagination ───

export function Pagination({
  page,
  totalPages,
  onPrev,
  onNext,
}: {
  page: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
}): React.JSX.Element {
  return (
    <div style={tableStyles.pagination}>
      <button
        type="button"
        disabled={page <= 1}
        onClick={onPrev}
        style={tableStyles.pageBtn}
      >
        {t("pagination.prev")}
      </button>
      <span style={tableStyles.pageInfo}>
        {page} / {totalPages || 1}
      </span>
      <button
        type="button"
        disabled={page >= totalPages}
        onClick={onNext}
        style={tableStyles.pageBtn}
      >
        {t("pagination.next")}
      </button>
    </div>
  );
}

// ─── ErrorBanner ───

export function ErrorBanner({
  message,
}: {
  message: string;
}): React.JSX.Element {
  return (
    <div style={tableStyles.errorBanner} role="alert">
      {message}
    </div>
  );
}

// ─── LoadingIndicator ───

export function LoadingIndicator(): React.JSX.Element {
  return <p style={tableStyles.loadingText}>{t("table.loading")}</p>;
}

// ─── Shared styles ───

export const tableStyles: Record<string, React.CSSProperties> = {
  filterBar: {
    display: "flex",
    gap: 8,
  },
  filterBtn: {
    padding: "6px 14px",
    fontSize: 13,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
    color: "var(--color-text-secondary)",
  },
  filterBtnActive: {
    background: "var(--color-primary)",
    color: "#ffffff",
    borderColor: "var(--color-primary)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 14,
  },
  th: {
    textAlign: "left",
    padding: "8px 12px",
    fontSize: 12,
    fontWeight: 600,
    color: "var(--color-text-secondary)",
    borderBottom: "1px solid var(--color-border)",
    textTransform: "uppercase" as const,
    letterSpacing: "0.04em",
  },
  td: {
    padding: "10px 12px",
    borderBottom: "1px solid var(--color-border)",
    verticalAlign: "top",
  },
  row: {
    transition: "background 0.1s",
  },
  expandedCell: {
    padding: 0,
    borderBottom: "1px solid var(--color-border)",
  },
  detailPanel: {
    padding: "16px 24px",
    background: "var(--color-bg-secondary)",
  },
  detailSection: {
    marginBottom: 12,
  },
  detailTitle: {
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 8,
    color: "var(--color-text-secondary)",
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "4px 0",
    fontSize: 13,
  },
  detailActions: {
    display: "flex",
    gap: 8,
    marginTop: 12,
  },
  actionBtn: {
    padding: "6px 14px",
    fontSize: 13,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
    color: "var(--color-text)",
  },
  dangerBtn: {
    borderColor: "var(--color-danger)",
    color: "var(--color-danger)",
  },
  actionMsg: {
    fontSize: 13,
    color: "var(--color-primary)",
    marginTop: 8,
  },
  badge: {
    fontSize: 12,
    fontWeight: 500,
  },
  pagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    marginTop: 16,
  },
  pageBtn: {
    padding: "6px 14px",
    fontSize: 13,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
    color: "var(--color-text)",
  },
  pageInfo: {
    fontSize: 13,
    color: "var(--color-text-secondary)",
  },
  errorBanner: {
    padding: "10px 14px",
    marginBottom: 16,
    background: "var(--color-danger-bg)",
    border: "1px solid var(--color-danger)",
    borderRadius: 6,
    fontSize: 13,
    color: "var(--color-danger)",
  },
  loadingText: {
    fontSize: 14,
    color: "var(--color-text-secondary)",
    textAlign: "center" as const,
    padding: "40px 0",
  },
  emptyText: {
    fontSize: 14,
    color: "var(--color-text-secondary)",
    textAlign: "center" as const,
    padding: "40px 0",
  },
  searchInput: {
    padding: "6px 12px",
    fontSize: 13,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    width: 200,
  },
  searchBtn: {
    padding: "6px 14px",
    fontSize: 13,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
    color: "var(--color-text)",
  },
};
